import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// material import
import { Box, Button, Grid2, Typography } from "@mui/material";
import Header2 from "../../ui-components/headerii";

// service import
import MpgsServices from "../../services/v1/mpgsServices";
import { formatDateForDisplay } from "../../utils/dateUtils";

const OrderPay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reservationData = location.state?.reservationDetails || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => formatDateForDisplay(dateString);

  // Extract and decode URL data if present
  const extractUrlData = () => {
    const queryParams = new URLSearchParams(location.search);
    const encodedData = queryParams.get("data");

    if (encodedData) {
      try {
        const jsonString = atob(encodedData); // Decode base64
        return JSON.parse(jsonString);
      } catch (error) {
        console.error("Failed to decode URL data:", error);
        return null;
      }
    }
    return null;
  };

  // Handle URL data on component mount
  useEffect(() => {
    const urlData = extractUrlData();
    if (urlData && !location.state?.reservationDetails) {
      // Map the URL data to the expected format
      const mappedData = {
        reservation_number: urlData.reservation_number,
        fullname: urlData.fullname,
        package_title: urlData.package_title,
        date: urlData.date,
        package_price: urlData.package_price,
        payment_type: urlData.payment_type,
        payment_option: urlData.payment_option,
        donor_email: urlData.donor_email,
        donorID: urlData.donorID,
      };

      // Store payment details in sessionStorage
      sessionStorage.setItem(
        "paymentDetails",
        JSON.stringify({
          reservation_number: urlData.reservation_number,
          donor_name: urlData.fullname,
          package_name: urlData.package_title,
          reserved_date: formatDateForDisplay(urlData.date),
          total: `Rs. ${urlData.package_price}`,
          payment_method: `${urlData.payment_type}(${urlData.payment_option})`,
          donor_email: urlData.donor_email,
          donorID: urlData.donorID,
        })
      );

      // Update location state with mapped data
      navigate(location.pathname, {
        state: { reservationDetails: mappedData },
        replace: true,
      });
    }
  }, [location.search]);

  // Load Mastercard Hosted Checkout Script
  const loadCheckoutScript = () => {
    return new Promise((resolve, reject) => {
      // Remove any existing checkout scripts to prevent conflicts
      const existingScript = document.getElementById("checkout-script");
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement("script");
      script.id = "checkout-script";
      // Make sure this URL matches your MPGS environment
      script.src =
        "https://cbcmpgs.gateway.mastercard.com/static/checkout/checkout.min.js";
      script.async = true;

      script.onload = () => {
        if (window.Checkout) {
          resolve(window.Checkout);
        } else {
          reject(new Error("Checkout not initialized after script load"));
        }
      };

      script.onerror = () => {
        reject(new Error("Failed to load checkout script"));
      };

      document.body.appendChild(script);
    });
  };

  // Fetch session ID from the backend
  const handlePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      await loadCheckoutScript();

      // This is the actual MPGS order id -- generated once here and reused
      // below for both the checkout session and what we remember for the
      // confirmation page. It must be the same value in both places: the
      // confirmation page needs it (not MPGS's resultIndicator, which is only
      // a client-side redirect-matches-session check, not a real order id) to
      // ask the backend to independently verify the payment against MPGS
      // before treating it as paid.
      const orderId = `ORDER-${reservationData.reservation_number}-${Date.now()}`;

      // Store payment details for confirmation page
      sessionStorage.setItem(
        "paymentDetails",
        JSON.stringify({
          reservation_number: reservationData.reservation_number,
          donor_name: reservationData.fullname,
          package_name: reservationData.package_title,
          reserved_date: formatDateForDisplay(reservationData.date),
          total: `Rs. ${reservationData.package_price}`,
          payment_method: ` ${reservationData.payment_type}(${reservationData.payment_option})`,
          donor_email: reservationData.donor_email,
          donorID: reservationData.donorID,
          orderId,
        })
      );

      const response = await MpgsServices.initiateCheckout({
        amount: reservationData.package_price, // Amount from reservation data
        currency: "LKR",
        order_id: orderId,
        description: `Payment for ${reservationData.package_title}`,
        return_url: `${window.location.origin}/confirmation`,
        cancel_url: `${window.location.origin}/order-pay`,
        merchant_name: "Suwa Arana",
        reservation_number: reservationData.reservation_number,
      });

      if (!response.data?.session?.id) {
        throw new Error("Failed to get valid session ID");
      }

      const sessionId = response.data.session.id;
      const successIndicator = response.data.successIndicator;

      // Store success indicator for later verification
      if (response.data.successIndicator) {
        sessionStorage.setItem("mpgsSuccessIndicator", successIndicator);
      }

      // Configure Checkout
      window.Checkout.configure({
        session: {
          id: sessionId,
        },
      });

      window.Checkout.showPaymentPage();
    } catch (err) {
      console.error("Payment Error:", err);
      setError(err.message || "Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Payment completion handler
  const handlePaymentCompletion = async (result) => {
    console.log("Payment Completion triggered:", result);
    const storedSuccessIndicator = sessionStorage.getItem(
      "mpgsSuccessIndicator"
    );

    if (result.success && result.successIndicator === storedSuccessIndicator) {
      const transactionId = result.transaction?.id;

      if (!transactionId) {
        setError("Transaction ID not found in payment response!");
        return;
      }

      try {
        // Payment successful
        navigate("/confirmation", {
          state: {
            success: true,
            orderId: reservationData.reservation_number,
            transactionId: transactionId,
          },
        });
      } catch (error) {
        console.error("Failed to store transaction:", error);
        setError("Payment was successful, but transaction storage failed.");
      }
    } else {
      setError("Payment was not successful. Please try again.");
    }
  };

  useEffect(() => {
    // Add completion handler to window object
    window.paymentCompleteCallback = handlePaymentCompletion;

    return () => {
      // Cleanup
      delete window.paymentCompleteCallback;
      sessionStorage.removeItem("mpgsSuccessIndicator");
    };
  }, []);

  return (
    <Box>
      <Header2 />
      <Typography
        variant="h5"
        sx={{
          mt: 3,
          fontWeight: "semibold",
          fontVariantCaps: "all-petite-caps",
          textAlign: "center",
        }}
      >
        Pay for Reservation
      </Typography>
      <Grid2 container justifyContent="center">
        <Grid2 item size={{ xs: 8.2 }} sx={{ mt: 3 }}>
          <Grid2 container>
            <Grid2
              item
              size={{ xs: 2 }}
              sx={{ p: 1, borderRight: "1px dashed lightgrey" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontVariantCaps: "all-small-caps",
                  fontSize: 14,
                  color: "grey",
                }}
              >
                Reservation No
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                {reservationData.reservation_number}
              </Typography>
            </Grid2>

            <Grid2
              item
              size={{ xs: 2 }}
              sx={{ p: 1, borderRight: "1px dashed lightgrey" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontVariantCaps: "all-small-caps",
                  fontSize: 14,
                  color: "grey",
                }}
              >
                Donor Name
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                {reservationData.fullname}
              </Typography>
            </Grid2>
            <Grid2
              item
              size={{ xs: 2 }}
              sx={{ p: 1, borderRight: "1px dashed lightgrey" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontVariantCaps: "all-small-caps",
                  fontSize: 14,
                  color: "grey",
                }}
              >
                Package
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                {reservationData.package_title}
              </Typography>
            </Grid2>
            <Grid2
              item
              size={{ xs: 2 }}
              sx={{ p: 1, borderRight: "1px dashed lightgrey" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontVariantCaps: "all-small-caps",
                  fontSize: 14,
                  color: "grey",
                }}
              >
                Date
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                {reservationData.date}
              </Typography>
            </Grid2>
            <Grid2
              item
              size={{ xs: 2 }}
              sx={{ p: 1, borderRight: "1px dashed lightgrey" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontVariantCaps: "all-small-caps",
                  fontSize: 14,
                  color: "grey",
                }}
              >
                Total
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                Rs. {reservationData.package_price}
              </Typography>
            </Grid2>
            <Grid2 item size={{ xs: 2 }} sx={{ p: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontVariantCaps: "all-small-caps",
                  fontSize: 14,
                  color: "grey",
                }}
              >
                Payment Method
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                {reservationData.payment_type}({reservationData.payment_option})
              </Typography>
            </Grid2>
          </Grid2>

          {error && <Typography color="error">{error}</Typography>}

          <Grid2 container align="center">
            <Grid2 item size={{ xs: 12 }}>
              <Button
                variant="contained"
                color="secondary"
                sx={{ mt: 5 }}
                onClick={handlePayment}
                disabled={loading}
              >
                {loading ? "Processing" : "Proceed to Payment"}
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default OrderPay;
